import { useState, useEffect } from "react";
import HomeScreen from "./components/HomeScreen";
import QuizScreen from "./components/QuizScreen";
import ResultsScreen from "./components/ResultsScreen";
import HistoryScreen from "./components/HistoryScreen";
import CDLManualViewer from "./components/CDLManualViewer";
import StudyWithGuideLayout from "./components/StudyWithGuideLayout";
import AnalyticsDashboard from "./components/AnalyticsDashboard";
import StudyPlanning from "./components/StudyPlanning";
import AirBrakeInspector from "./components/AirBrakeInspector";
import AirBrakeSkillsTest from "./components/AirBrakeSkillsTest";
import CDLTutorial from "./components/CDLTutorial";
import PreTripInspection from "./components/PreTripInspection";
import { saveSession } from "./utils/history";
import { loadPracticeSession, clearPracticeSession } from "./utils/practiceSession";
import { getWeakQuestions } from "./utils/weakAreas";
import { getMbtaTrainingQuestions, getMbtaGKQuestions, getMbtaAirBrakesQuestions, getMbtaPassengerQuestions, QUESTION_BANKS, MBTA_SECTIONS, shuffle } from "./data/index";
import "./App.css";

const VIEWS = { HOME: "home", QUIZ: "quiz", RESULTS: "results", HISTORY: "history", GUIDE: "guide", STUDY_MODE: "studyMode", ANALYTICS: "analytics", STUDY_PLANNING: "studyPlanning", AIR_BRAKE_INSPECTOR: "airBrakeInspector", AIR_BRAKE_SKILLS_TEST: "airBrakeSkillsTest", CDL_TUTORIAL: "cdlTutorial", PRE_TRIP_INSPECTION: "preTripInspection" };

export default function App() {
  const [view, setView]         = useState(VIEWS.HOME);
  const [quizState, setQuizState] = useState(null);
  const [preTripDefaultView, setPreTripDefaultView] = useState("document");

  const startPractice = (section, timeMinutes) => {
    const saved = loadPracticeSession(section);
    if (saved && Object.keys(saved.answers).length > 0) {
      const resume = window.confirm(
        `You have a saved "${section}" session at question ${saved.currentIdx + 1}/${saved.questions.length} with ${Object.keys(saved.answers).length} answered.\n\nResume where you left off?`
      );
      if (resume) {
        setQuizState({ section, mode: "practice", examCount: null, timeMinutes, answers: {}, questions: [], customQuestions: saved.questions, initialAnswers: saved.answers, initialFlagged: saved.flagged ?? {}, initialIdx: saved.currentIdx });
        setView(VIEWS.QUIZ);
        return;
      }
      clearPracticeSession(section);
    }
    setQuizState({ section, mode: "practice", examCount: null, timeMinutes, answers: {}, questions: [] });
    setView(VIEWS.QUIZ);
  };

  const startExam = (section, examCount, timeMinutes) => {
    setQuizState({ section, mode: "exam", examCount, timeMinutes, answers: {}, questions: [] });
    setView(VIEWS.QUIZ);
  };

  const MBTA_CATEGORY_CONFIG = {
    gk:  { label: "MBTA Training — General Knowledge", timeMinutes: 90,  getQs: getMbtaGKQuestions },
    ab:  { label: "MBTA Training — Air Brakes",        timeMinutes: 45,  getQs: getMbtaAirBrakesQuestions },
    pa:  { label: "MBTA Training — Passenger",         timeMinutes: 35,  getQs: getMbtaPassengerQuestions },
    all: { label: "MBTA Training Drills",              timeMinutes: 120, getQs: getMbtaTrainingQuestions },
  };

  const startMbtaTraining = (category = "all") => {
    const { label: section, timeMinutes, getQs } = MBTA_CATEGORY_CONFIG[category] ?? MBTA_CATEGORY_CONFIG.all;
    const saved = loadPracticeSession(section);
    if (saved && Object.keys(saved.answers).length > 0) {
      const resume = window.confirm(
        `You have a saved "${section}" session at question ${saved.currentIdx + 1}/${saved.questions.length} with ${Object.keys(saved.answers).length} answered.\n\nResume where you left off?`
      );
      if (resume) {
        setQuizState({ section, mode: "practice", examCount: null, timeMinutes, answers: {}, questions: [], customQuestions: saved.questions, initialAnswers: saved.answers, initialFlagged: saved.flagged ?? {}, initialIdx: saved.currentIdx, mbtaCategory: category });
        setView(VIEWS.QUIZ);
        return;
      }
      clearPracticeSession(section);
    }
    const qs = getQs();
    setQuizState({ section, mode: "practice", examCount: null, timeMinutes, answers: {}, questions: [], customQuestions: qs, mbtaCategory: category });
    setView(VIEWS.QUIZ);
  };

  // Drill Weak Spots: pull the weakest questions from all 3 MBTA banks combined
  const startWeakDrill = () => {
    const allWeak = shuffle([
      ...getWeakQuestions(QUESTION_BANKS[MBTA_SECTIONS[0]], 30),
      ...getWeakQuestions(QUESTION_BANKS[MBTA_SECTIONS[1]], 20),
      ...getWeakQuestions(QUESTION_BANKS[MBTA_SECTIONS[2]], 20),
    ]);

    if (allWeak.length === 0) {
      alert("No weak spots found yet! Complete a few Practice or Exam sessions first, then your problem questions will appear here.");
      return;
    }

    setQuizState({
      section: "Weak Spots Drill",
      mode: "practice",
      examCount: null,
      timeMinutes: 60,
      answers: {},
      questions: [],
      customQuestions: allWeak,
      isWeakDrill: true,
    });
    setView(VIEWS.QUIZ);
  };

  const finishQuiz = (answers, questions) => {
    const updated = { ...quizState, answers, questions };
    setQuizState(updated);
    saveSession({ section: updated.section, mode: updated.mode, questions, answers });
    setView(VIEWS.RESULTS);
  };

  const retry = () => {
    const { section, mode, examCount, timeMinutes, customQuestions, mbtaCategory, isWeakDrill } = quizState;
    if (isWeakDrill) { startWeakDrill(); return; }
    if (customQuestions && mbtaCategory) { startMbtaTraining(mbtaCategory); return; }
    if (mode === "exam") startExam(section, examCount, timeMinutes);
    else startPractice(section, timeMinutes);
  };

  const retryFromHistory = (section, mode) => {
    import("./data/index").then(({ EXAM_CONFIG, PRACTICE_CONFIG, SECTIONS }) => {
      const secKey = Object.values(SECTIONS).find((s) => s === section);
      if (!secKey) { setView(VIEWS.HOME); return; }
      if (mode === "exam") {
        const cfg = EXAM_CONFIG[secKey];
        startExam(secKey, cfg.count, cfg.timeMinutes);
      } else {
        const cfg = PRACTICE_CONFIG[secKey];
        startPractice(secKey, cfg.timeMinutes);
      }
    });
  };

  // Check for pending mode from sessionStorage (for opening in new tab)
  useEffect(() => {
    const pendingMode = sessionStorage.getItem('pendingMode');
    const splitMode = sessionStorage.getItem('splitMode');

    if (splitMode) {
      sessionStorage.removeItem('splitMode');
      if (splitMode === 'practice') {
        const saved = loadPracticeSession('General Knowledge');
        if (saved && Object.keys(saved.answers).length > 0) {
          setQuizState({ section: 'General Knowledge', mode: "practice", examCount: null, timeMinutes: 90, answers: {}, questions: [], customQuestions: saved.questions, initialAnswers: saved.answers, initialFlagged: saved.flagged ?? {}, initialIdx: saved.currentIdx });
        } else {
          setQuizState({ section: 'General Knowledge', mode: "practice", examCount: null, timeMinutes: 90, answers: {}, questions: [] });
        }
        setView(VIEWS.STUDY_MODE);
      } else if (splitMode === 'exam') {
        setQuizState({ section: 'General Knowledge', mode: "exam", examCount: 50, timeMinutes: 60, answers: {}, questions: [] });
        setView(VIEWS.STUDY_MODE);
      } else if (splitMode === 'history') {
        setView(VIEWS.HISTORY);
      }
    } else if (pendingMode) {
      sessionStorage.removeItem('pendingMode');
      if (pendingMode === 'practice') {
        startPractice('General Knowledge', 90);
      } else if (pendingMode === 'exam') {
        startExam('General Knowledge', 50, 60);
      } else if (pendingMode === 'history') {
        setView(VIEWS.HISTORY);
      }
    }
  }, []);

  return (
    <div className="app">
      {view === VIEWS.HOME && (
        <HomeScreen
          onStartPractice={startPractice}
          onStartExam={startExam}
          onStartMbtaTraining={startMbtaTraining}
          onStartWeakDrill={startWeakDrill}
          onGoHistory={() => setView(VIEWS.HISTORY)}
          onGoGuide={() => setView(VIEWS.GUIDE)}
          onGoAnalytics={() => setView(VIEWS.ANALYTICS)}
          onGoStudyPlanning={() => setView(VIEWS.STUDY_PLANNING)}
          onGoAirBrakeInspector={() => setView(VIEWS.AIR_BRAKE_INSPECTOR)}
          onGoAirBrakeSkillsTest={() => setView(VIEWS.AIR_BRAKE_SKILLS_TEST)}
          onGoCDLTutorial={() => setView(VIEWS.CDL_TUTORIAL)}
          onGoPreTripInspection={() => setView(VIEWS.PRE_TRIP_INSPECTION)}
        />
      )}
      {view === VIEWS.QUIZ && quizState && (
        <QuizScreen
          section={quizState.section}
          mode={quizState.mode}
          examCount={quizState.examCount}
          timeLimitMinutes={quizState.timeMinutes}
          customQuestions={quizState.customQuestions}
          initialAnswers={quizState.initialAnswers}
          initialFlagged={quizState.initialFlagged}
          initialIdx={quizState.initialIdx}
          onFinish={finishQuiz}
          onHome={() => setView(VIEWS.HOME)}
        />
      )}
      {view === VIEWS.RESULTS && quizState && (
        <ResultsScreen
          section={quizState.section}
          answers={quizState.answers}
          questions={quizState.questions}
          mode={quizState.mode}
          onRetry={retry}
          onHome={() => setView(VIEWS.HOME)}
          onGoHistory={() => setView(VIEWS.HISTORY)}
        />
      )}
      {view === VIEWS.HISTORY && (
        <HistoryScreen
          onHome={() => setView(VIEWS.HOME)}
          onRetry={retryFromHistory}
        />
      )}
      {view === VIEWS.GUIDE && (
        <CDLManualViewer
          onHome={() => setView(VIEWS.HOME)}
          onStartPractice={startPractice}
          onStartExam={startExam}
          onGoHistory={() => setView(VIEWS.HISTORY)}
        />
      )}
      {view === VIEWS.STUDY_MODE && quizState && (
        <StudyWithGuideLayout onClose={() => setView(VIEWS.HOME)}>
          <QuizScreen
            section={quizState.section}
            mode={quizState.mode}
            examCount={quizState.examCount}
            timeLimitMinutes={quizState.timeMinutes}
            customQuestions={quizState.customQuestions}
            initialAnswers={quizState.initialAnswers}
            initialFlagged={quizState.initialFlagged}
            initialIdx={quizState.initialIdx}
            onFinish={finishQuiz}
            onHome={() => setView(VIEWS.HOME)}
          />
        </StudyWithGuideLayout>
      )}
      {view === VIEWS.ANALYTICS && (
        <AnalyticsDashboard onHome={() => setView(VIEWS.HOME)} />
      )}
      {view === VIEWS.STUDY_PLANNING && (
        <StudyPlanning
          onHome={() => setView(VIEWS.HOME)}
          onStartPractice={startPractice}
          onGoPreTripInspection={() => { setPreTripDefaultView('document'); setView(VIEWS.PRE_TRIP_INSPECTION); }}
          onGoPreTripFlashcards={() => { setPreTripDefaultView('flashcards'); setView(VIEWS.PRE_TRIP_INSPECTION); }}
          onGoPreTripQuiz={() => { setPreTripDefaultView('quiz'); setView(VIEWS.PRE_TRIP_INSPECTION); }}
          onGoPreTripStudyPlan={() => { setPreTripDefaultView('studyplan'); setView(VIEWS.PRE_TRIP_INSPECTION); }}
        />
      )}
      {view === VIEWS.AIR_BRAKE_INSPECTOR && (
        <AirBrakeInspector
          onHome={() => setView(VIEWS.HOME)}
        />
      )}
      {view === VIEWS.AIR_BRAKE_SKILLS_TEST && (
        <AirBrakeSkillsTest
          onHome={() => setView(VIEWS.HOME)}
        />
      )}
      {view === VIEWS.CDL_TUTORIAL && (
        <CDLTutorial
          onHome={() => setView(VIEWS.HOME)}
        />
      )}
      {view === VIEWS.PRE_TRIP_INSPECTION && (
        <PreTripInspection
          onHome={() => setView(VIEWS.HOME)}
          defaultView={preTripDefaultView}
        />
      )}
    </div>
  );
}
